"use client";

import { useTranslations } from "next-intl";
import { css, cx } from "styled-system/css";
import { flex } from "styled-system/patterns";

export interface ClaimState {
  /** Unlock time passed AND the lock id is known — the claim link goes live. */
  claimable: boolean;
  pending: boolean;
  onClaim: () => void;
}

interface FundsUnlockFooterProps {
  /** Pre-formatted metric strings. */
  lockedLabel: string;
  unlocksAtLabel: string;
  claim?: ClaimState;
}

export function FundsUnlockFooter({ lockedLabel, unlocksAtLabel, claim }: FundsUnlockFooterProps) {
  const t = useTranslations("firstTradeOnUs.details.settle");

  return (
    <div className={footerStyles}>
      <span className={unlockTextStyles}>
        {t.rich("fundsUnlock", {
          date: (chunks) => <span className={unlockDateStyles}>{chunks}</span>,
          dateValue: unlocksAtLabel,
        })}
      </span>
      {claim?.claimable ? (
        <button
          type="button"
          onClick={claim.onClaim}
          disabled={claim.pending}
          data-testid="fft-claim-unlock"
          className={cx(claimTextStyles, claimActiveStyles)}
        >
          {t("claimCta", { amount: lockedLabel })}
        </button>
      ) : (
        <span className={cx(claimTextStyles, claimLockedStyles)}>
          {t("claimLocked", { amount: lockedLabel })}
        </span>
      )}
    </div>
  );
}

const footerStyles = flex({
  gap: "8",
  px: "16",
  py: "12",
  minH: "48",
  wrap: "wrap",
  align: "center",
  borderTopWidth: "100",
  borderBottomWidth: "100",
  borderTopStyle: "solid",
  borderBottomStyle: "solid",
  borderTopColor: "border.primary",
  borderBottomColor: "border.primary",
});

const claimTextStyles = css({
  textStyle: "highlight.md",
  textDecoration: "underline",
  textUnderlineOffset: "2",
  _hover: { textDecoration: "none" },
  _disabled: { color: "text.disabled", cursor: "default" },
});

const claimActiveStyles = css({ color: "text.primary", cursor: "pointer" });
const claimLockedStyles = css({ color: "text.disabled" });

const unlockTextStyles = css({ textStyle: "highlight.md", color: "text.disabled" });
const unlockDateStyles = css({ color: "text.tertiary" });
